import React, { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import "./BusinessProfile.scss";
import { Link } from "react-router-dom";
import { getUserById, revokeVerification, approveApplication } from "../../../services/userService";

function BusinessProfile() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const userId = location.state?.userId || searchParams.get("id");

  const [revokeSubject, setRevokeSubject] = useState("");
  const [revokeMessage, setRevokeMessage] = useState("");
  const revokeModalRef = useRef(null);
  const verifyModalRef = useRef(null);

  const revokeReasons = [
    "License Expired",
    "Incorrect data",
    "Invalid Documentation",
    "Fraudulent Activity",
    "Other",
  ];

  // Get user data from location state or fetch from API
  const {
    data: userData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => getUserById(userId),
    enabled: !!userId && !location.state?.userData,
    retry: 1,
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Failed to load business data");
    },
  });

  // Use data from state or API
  const user = location.state?.userData || userData?.data || userData;

  // Revoke verification mutation
  const revokeMutation = useMutation({
    mutationFn: ({ userId, revokeData }) =>
      revokeVerification(userId, revokeData),
    onSuccess: (data) => {
      toast.success(data?.message || "Verification revoked successfully");

      // Reset form fields
      setRevokeSubject("");
      setRevokeMessage("");

      // Close modal using Bootstrap API
      if (revokeModalRef.current) {
        const modalElement = revokeModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Refetch user data and users list
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to revoke verification";
      toast.error(errorMessage);
    },
  });

  const handleRevoke = () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    if (!revokeSubject.trim()) {
      toast.error("Please select a reason");
      return;
    }

    if (!revokeMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }

    revokeMutation.mutate({
      userId,
      revokeData: {
        subject: revokeSubject.trim(),
        message: revokeMessage.trim(),
      },
    });
  };

  const handleReasonChange = (reason) => {
    setRevokeSubject(reason);
  };

  // Approve application mutation
  const approveApplicationMutation = useMutation({
    mutationFn: (userId) => approveApplication(userId),
    onSuccess: (data) => {
      toast.success(data?.message || "Application approved successfully");

      // Close modal using Bootstrap API
      if (verifyModalRef.current) {
        const modalElement = verifyModalRef.current;
        const modal = window.bootstrap?.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        } else {
          const $modal = window.$ ? window.$(modalElement) : null;
          if ($modal) {
            $modal.modal("hide");
          }
        }
      }

      // Refetch user data and users list
      if (userId) {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve application";
      toast.error(errorMessage);
    },
  });

  const handleApproveApplication = () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }

    approveApplicationMutation.mutate(userId);
  };

  // Reset form when modal is closed
  useEffect(() => {
    const revokeModalElement = revokeModalRef.current;

    const handleRevokeModalHidden = () => {
      setRevokeSubject("");
      setRevokeMessage("");
    };

    if (revokeModalElement) {
      revokeModalElement.addEventListener(
        "hidden.bs.modal",
        handleRevokeModalHidden
      );
    }

    return () => {
      if (revokeModalElement) {
        revokeModalElement.removeEventListener(
          "hidden.bs.modal",
          handleRevokeModalHidden
        );
      }
    };
  }, []);

  // Transform user data to match the display structure
  const profileData = useMemo(() => {
    if (!user) return null;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) + " | " + date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } catch (e) {
        return dateString;
      }
    };

    const kycStatus = user.kycVerification?.charAt(0).toUpperCase() +
                     user.kycVerification?.slice(1) || "Unverified";

    const accountStatus = user.status?.charAt(0).toUpperCase() +
                         user.status?.slice(1) || "N/A";
    const isStatusApproved = user.status?.toLowerCase() === "approved" || user.status?.toLowerCase() === "verified";

    return {
      sections: [
        {
          title: "Basic Info",
          items: [
            { label: "Name", value: user.businessName || user.name || user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A" },
            { label: "Email", value: user.email || "N/A" },
            { label: "Joined", value: formatDate(user.createdAt) },
            { label: "Country", value: user.country || "N/A" },
            { label: "Dealership", value: user.dealership || user.businessType || "N/A" },
            { label: "Contact", value: user.phone || user.contact || "N/A" },
          ],
        },
        {
          title: "Verification (KYC)",
          items: [
            {
              label: "Status",
              value: kycStatus,
              type: user.kycVerification === "verified" ? "status-approved" : "status",
            },
            { label: "Verification Type", value: user.verificationType || "Manual" },
            { label: "Verified On", value: user.verifiedAt ? formatDate(user.verifiedAt) : "N/A" },
          ],
        },
        {
          title: "Verification",
          items: [
            {
              label: "Status",
              value: accountStatus,
              type: isStatusApproved ? "status-approved" : "status",
            },
            { label: "Verified By", value: user.verifiedBy || "Admin" },
          ],
        },
        {
          title: "Reports Summary",
          items: [
            { label: "Active Reports", value: user.activeReports || "0" },
            { label: "Resolved Reports", value: user.resolvedReports || "0" },
            { label: "Pending", value: user.pendingReports || "0" },
          ],
        },
      ],
    };
  }, [user]);

  if (isLoading) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <p>Loading business data...</p>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <p>Error loading business data. Please try again.</p>
        <Link to="/user-management">Back to User Management</Link>
      </div>
    );
  }

  const displayName = user.businessName || user.name || user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Business";
  const businessCode = `B${user._id?.slice(-4) || "0000"}`;
  return (
    <React.Fragment>
      <header className="reported-plate-panel-header items-center gap-3 flex content-justify-between">
        <div className="left-side">
          <Link to="/user-management">
            <img src={process.env.PUBLIC_URL + "/images/back.svg"} alt="back" />
          </Link>
          <h4 className="title">Business Profile</h4>
        </div>

        <div className="right-side">
          <button
            className="approved"
            data-bs-toggle="modal"
            data-bs-target="#verifyModal"
          >
            Verify Business
          </button>
          <button
            className="info"
            data-bs-toggle="modal"
            data-bs-target="#revokeModal"
          >
            Revoke Verification
          </button>
          <button
            className="revoke"
            data-bs-toggle="modal"
            data-bs-target="#rejectModal"
          >
            Reject Verification
          </button>
        </div>
      </header>
      <div className="report-id-wrapper">
        <div className="flex align-items-center justify-content-between">
          <p className="report-id-name">Business Profile</p>
          <p className="report-id">{displayName} ({businessCode})</p>
        </div>
      </div>
      <div className="business-profile">
        <div className="row">
          {profileData?.sections.map((section, index) => {
            let colSize = "12"; // default column size

            // Adjust columns for specific sections if needed
            if (section.title === "Plates Owned") colSize = "12";

            return (
              <div key={index} className={`col-lg-12 col-xl-${colSize}`}>
                <div className="card">
                  <h4>{section.title}</h4>
                  <div className="inner-content">
                    <div className="row">
                      {section.items.map((item, i) => {
                        // Check if the item is a plate card
                        if (item.itemType === "plate-card") {
                          return (
                            <div key={i} className="col-lg-4 col-xl-4 col-md-6">
                              <div className="plate-card">
                                <div className="plate-image">
                                  <img src={item.image} alt={item.label} />
                                </div>
                                <div className="plate-info">
                                  <h5>{item.label}</h5>
                                  <p
                                    className={`status-badge ${
                                      item.type === "status-approved"
                                        ? "approved"
                                        : "pending"
                                    }`}
                                  >
                                    <span className="dot"></span> {item.value}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Default item rendering
                        return (
                          <div key={i} className="col-lg-4 col-xl-4 col-md-6">
                            <div className="content-wrapper">
                              <img
                                src={process.env.PUBLIC_URL + "/images/user-icon.svg"}
                                alt="icon"
                                className="icon"
                              />
                              <div className="content">
                                <h5>{item.label}</h5>
                                {item.type === "status" && (
                                  <p className="status-badge pending">
                                    <span className="dot"></span> {item.value}
                                  </p>
                                )}
                                {item.type === "status-approved" && (
                                  <p className="status-badge approved">
                                    <span className="dot"></span> {item.value}
                                  </p>
                                )}
                                {!item.type && <p>{item.value}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revoke Modal */}
      <div
        ref={verifyModalRef}
        className="modal fade"
        id="verifyModal"
        tabIndex="-1"
        aria-labelledby="verifyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="verifyModalLabel">
                Approve Verification
              </h5>
            </div>

            <div className="modal-body">
              <label className="form-label mb-0">
                Confirm that all submitted documents are valid and approve this
                business profile.
              </label>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={approveApplicationMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="green-btn"
                onClick={handleApproveApplication}
                disabled={approveApplicationMutation.isPending}
              >
                {approveApplicationMutation.isPending
                  ? "Approving..."
                  : "Approve Verification"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* revoke Modal */}
      <div
        ref={revokeModalRef}
        className="modal fade"
        id="revokeModal"
        tabIndex="-1"
        aria-labelledby="revokeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="revokeModalLabel">
                Revoke Verification
              </h5>
            </div>

            <div className="modal-body">
              <h3>
                Are you sure you want to remove {displayName}'s verified (blue
                tick) badge?
              </h3>
              <label className="form-label">
                This will reset their KYC status to unverified
              </label>
              <label className="form-label">Select Reason</label>

              {/* Radio buttons - only one selectable */}
              {revokeReasons.map((reasonOption, index) => (
                <div key={index} className="form-check mb-2">
                  <input
                    type="radio"
                    name="revokeReason"
                    id={`reason${index + 1}`}
                    checked={revokeSubject === reasonOption}
                    onChange={() => handleReasonChange(reasonOption)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`reason${index + 1}`}
                  >
                    {reasonOption}
                  </label>
                </div>
              ))}

              {/* Textarea */}
              <label className="form-label">Message</label>
              <textarea
                className="form-control"
                rows="5"
                placeholder="Write here"
                value={revokeMessage}
                onChange={(e) => setRevokeMessage(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
                disabled={revokeMutation.isPending}
              >
                Close
              </button>

              <button
                type="button"
                className="info-btn"
                onClick={handleRevoke}
                disabled={revokeMutation.isPending}
              >
                {revokeMutation.isPending
                  ? "Revoking..."
                  : "Revoke Verification"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Restore Access Modal */}
      <div
        className="modal fade"
        id="rejectModal"
        tabIndex="-1"
        aria-labelledby="rejectModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="rejectModalLabel">
                Reject Application
              </h5>
            </div>

            <div className="modal-body">
              <h3>Provide a reason for rejection.</h3>
              <label className="form-label">Reason</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Write here"
              ></textarea>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                Close
              </button>

              <button type="button" className="red-btn">
                Reject Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default BusinessProfile;
